import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct, fmtDate } from "../lib/api";
import { AlertTriangle, AlertCircle, ShieldCheck, ArrowUp, ArrowDown, ArrowUpDown, Calendar as CalendarIcon, Hand, Link2, FileText } from "lucide-react";

// ────────────────────────────────────────────────────────────────────────
// Call-Risk Timeline — visualises how close each callable position is to
// its call_date, color-coded by risk tier. Backend math is in
// /app/backend/routes/portfolio.py → /api/portfolio/call-risk
// ────────────────────────────────────────────────────────────────────────

const TIER_META = {
  high: {
    label: "HIGH",
    cls: "bg-burgundy text-champagne",
    border: "border-burgundy",
    bar: "bg-burgundy",
    text: "text-burgundy",
    Icon: AlertCircle,
    blurb: "Callable now AND trading above call price. Issuer could redeem any day → you'd lose the premium.",
  },
  medium: {
    label: "MEDIUM",
    cls: "bg-gold text-graphite",
    border: "border-gold",
    bar: "bg-gold",
    text: "text-gold-dark",
    Icon: AlertTriangle,
    blurb: "Within 90 days of call date AND trading above call price. Monitor closely.",
  },
  low: {
    label: "LOW",
    cls: "bg-groen text-graphite",
    border: "border-groen",
    bar: "bg-groen",
    text: "text-groen-dark",
    Icon: ShieldCheck,
    blurb: "Either far from call date or trading at/below call price. Minimal call risk.",
  },
};

const SOURCE_ICON = { snaptrade: Link2, csv_import: FileText, manual: Hand };

// Sort indicator icon for a column header.
function getSortIcon(col, sort) {
  if (col?.sortable === false) return null;
  if (sort.key !== col?.key) return ArrowUpDown;
  return sort.dir === "asc" ? ArrowUp : ArrowDown;
}

// Days-until-call display string for the call-risk table.
function daysUntilCallText(p) {
  if (p.days_until_call == null) return "—";
  if (p.is_callable_now) return "CALLABLE";
  return p.days_until_call;
}

// Right-side caption beneath the timeline row.
function callableCaption(p) {
  if (p.is_callable_now) {
    return <span className="text-burgundy font-bold">CALLABLE NOW</span>;
  }
  if (p.days_until_call != null) {
    return <>{p.days_until_call} days to call</>;
  }
  return "No call date on file";
}

// Tier counts in filter pill bubbles.
function tierCount(tier, summary) {
  if (tier === "high") return summary.high_risk_count;
  if (tier === "medium") return summary.medium_risk_count;
  return summary.low_risk_count;
}

// Place a point on the timeline. Maps a date between purchase_date and
// maturity_date to a 0-100 percentage. Today gets its own anchor.
function pct(start, end, target) {
  try {
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    const t = new Date(target).getTime();
    if (!isFinite(s) || !isFinite(e) || e <= s) return null;
    return Math.max(0, Math.min(100, ((t - s) / (e - s)) * 100));
  } catch { return null; }
}

const COLUMNS = [
  { key: "ticker", label: "Ticker", align: "left", accessor: (r) => (r.ticker || "").toUpperCase() },
  { key: "risk_tier", label: "Risk", align: "left", accessor: (r) => ({ high: 0, medium: 1, low: 2 }[r.risk_tier]) },
  { key: "current_price", label: "Price", align: "right", accessor: (r) => r.current_price || 0 },
  { key: "call_price", label: "Call price", align: "right", accessor: (r) => r.call_price || 0 },
  { key: "call_date", label: "Call date", align: "left", accessor: (r) => r.call_date || "9999" },
  { key: "days_until_call", label: "Days", align: "right", accessor: (r) => r.days_until_call ?? 999999 },
  { key: "call_loss_total", label: "At risk", align: "right", accessor: (r) => r.call_loss_total || 0 },
  { key: "ytc", label: "YTC", align: "right", accessor: (r) => r.ytc ?? -1 },
  { key: "ytm", label: "YTM", align: "right", accessor: (r) => r.ytm ?? -1 },
];

export default function CallRisk() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "risk_tier", dir: "asc" });
  const [filterTier, setFilterTier] = useState("all"); // all | high | medium | low

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/portfolio/call-risk");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const positions = data?.positions || [];

  const filtered = useMemo(() => {
    let out = positions;
    if (filterTier !== "all") out = out.filter((p) => p.risk_tier === filterTier);
    if (sort.key) {
      const col = COLUMNS.find((c) => c.key === sort.key);
      if (col) {
        const acc = col.accessor;
        const dir = sort.dir === "desc" ? -1 : 1;
        out = [...out].sort((a, b) => {
          const va = acc(a), vb = acc(b);
          if (va < vb) return -1 * dir;
          if (va > vb) return 1 * dir;
          return 0;
        });
      }
    }
    return out;
  }, [positions, sort, filterTier]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::CALL_RISK</div>
        <h1 className="text-2xl font-bold tracking-tight text-graphite" data-testid="call-risk-page-title">Call Risk Timeline</h1>
        <p className="text-xs text-graphite/55 mt-1 max-w-2xl">
          Preferreds and babybonds let the issuer redeem early at the call price.
          If they trade above call price and the call date is near, you can lose the premium overnight.
        </p>
      </div>

      <div className="p-8 space-y-8">
        {loading ? (
          <div className="text-graphite/55 font-mono text-sm animate-pulse">Loading call-risk analysis…</div>
        ) : positions.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Summary stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone border border-stone">
              <SummaryStat
                testId="callrisk-stat-analyzed"
                label="Positions analyzed"
                value={data.summary.positions_analyzed}
                sub="Preferreds + babybonds only"
              />
              <SummaryStat
                testId="callrisk-stat-high"
                label="High risk"
                value={data.summary.high_risk_count}
                sub="Callable now + above call price"
                accent="text-burgundy"
                onClick={() => setFilterTier("high")}
              />
              <SummaryStat
                testId="callrisk-stat-medium"
                label="Medium risk"
                value={data.summary.medium_risk_count}
                sub="≤90 days + above call price"
                accent="text-gold-dark"
                onClick={() => setFilterTier("medium")}
              />
              <SummaryStat
                testId="callrisk-stat-total-at-risk"
                label="Total at risk"
                value={fmtMoney(data.summary.total_at_risk_usd)}
                sub="Premium lost if all called today"
                accent="text-burgundy"
              />
            </div>

            {/* Tier filter pills */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="callrisk-tier-filters">
              {["all", "high", "medium", "low"].map((t) => (
                <button
                  key={t}
                  data-testid={`callrisk-filter-${t}`}
                  onClick={() => setFilterTier(t)}
                  className={`text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1.5 border transition-colors ${
                    filterTier === t
                      ? "bg-graphite text-champagne border-graphite"
                      : "bg-champagne text-graphite/60 border-stone hover:text-graphite hover:bg-stone/40"
                  }`}
                >
                  {t}
                  {t !== "all" && (
                    <span className="ml-2 opacity-70">
                      {tierCount(t, data.summary)}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Timeline visualization */}
            <div className="border border-stone bg-champagne" data-testid="callrisk-timeline">
              <div className="px-5 py-3 border-b border-stone flex items-center justify-between">
                <div className="text-[10px] tracking-[0.3em] uppercase text-graphite/55 font-bold">// TIMELINE</div>
                <div className="text-[10px] tracking-widest font-mono text-graphite/45">{data.today}</div>
              </div>
              <div className="divide-y divide-stone">
                {filtered.map((p) => (
                  <TimelineRow key={p.id} pos={p} today={data.today} />
                ))}
                {filtered.length === 0 && (
                  <div className="p-6 text-center text-graphite/55 text-xs font-mono">No positions match this filter.</div>
                )}
              </div>
            </div>

            {/* Detail table */}
            <div className="border border-stone bg-champagne overflow-x-auto" data-testid="callrisk-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone/95 border-b border-stone">
                    {COLUMNS.map((col) => {
                      const isSorted = sort.key === col.key;
                      const SortIcon = getSortIcon(col, sort);
                      return (
                        <th
                          key={col.key}
                          data-testid={`callrisk-col-${col.key}`}
                          onClick={() => toggleSort(col.key)}
                          className={`px-3 py-3 text-[10px] tracking-[0.2em] uppercase font-bold cursor-pointer hover:text-paars-dark select-none ${
                            col.align === "right" ? "text-right" : "text-left"
                          } ${isSorted ? "text-paars-dark" : "text-graphite/60"}`}
                        >
                          <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""}`}>
                            {col.label}
                            {SortIcon && <SortIcon className={`w-3 h-3 ${isSorted ? "opacity-100" : "opacity-30"}`} strokeWidth={2.5} />}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const tier = TIER_META[p.risk_tier];
                    const SrcIcon = SOURCE_ICON[p.source] || Hand;
                    return (
                      <tr key={p.id} data-testid={`callrisk-row-${p.ticker}`} className="border-b border-stone/60 hover:bg-white">
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <SrcIcon className="w-3 h-3 text-graphite/40" />
                            <div>
                              <div className="font-mono font-semibold text-paars-dark">{p.ticker}</div>
                              <div className="text-[10px] text-graphite/55 truncate max-w-[160px]">{p.name || ""}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono tracking-widest ${tier.cls}`}>
                            <tier.Icon className="w-3 h-3" /> {tier.label}
                          </span>
                        </td>
                        <td className="px-3 py-3 font-mono text-right text-graphite">{p.current_price ? fmtMoney(p.current_price) : "—"}</td>
                        <td className="px-3 py-3 font-mono text-right text-graphite/80">{fmtMoney(p.call_price)}</td>
                        <td className="px-3 py-3 font-mono text-graphite/70">{p.call_date ? fmtDate(p.call_date) : "—"}</td>
                        <td className={`px-3 py-3 font-mono text-right ${p.is_callable_now ? "text-burgundy" : "text-graphite/70"}`}>
                          {daysUntilCallText(p)}
                        </td>
                        <td className={`px-3 py-3 font-mono text-right ${p.call_loss_total > 0 ? "text-burgundy" : "text-graphite/45"}`}>
                          {fmtMoney(p.call_loss_total)}
                        </td>
                        <td className={`px-3 py-3 font-mono text-right ${p.ytc != null && p.ytc < 0 ? "text-burgundy" : "text-gold-dark"}`}>{p.ytc != null ? fmtPct(p.ytc) : "—"}</td>
                        <td className="px-3 py-3 font-mono text-right text-paars-dark">{p.ytm != null ? fmtPct(p.ytm) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="border border-stone bg-champagne p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-graphite/55 font-bold mb-3">// HOW_TO_READ</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(TIER_META).map(([key, t]) => (
                  <div key={key} className={`p-4 border-l-2 ${t.border}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <t.Icon className={`w-4 h-4 ${t.text}`} strokeWidth={2.5} />
                      <span className={`text-[10px] tracking-[0.2em] uppercase font-bold ${t.text}`}>{t.label}</span>
                    </div>
                    <p className="text-xs text-graphite/65 leading-relaxed">{t.blurb}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryStat({ testId, label, value, sub, accent = "text-graphite", onClick }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`bg-champagne border border-stone p-5 flex flex-col justify-between min-h-[124px] ${onClick ? "cursor-pointer hover:bg-white transition-colors" : ""}`}
    >
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-graphite/55">{label}</div>
      <div>
        <div className={`text-3xl font-mono tracking-tight ${accent}`}>{value}</div>
        {sub && <div className="text-xs font-mono text-graphite/55 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

// Per-position timeline row: a horizontal bar from purchase_date → maturity_date
// with markers for today and call_date. Risk tier sets the bar color.
function TimelineRow({ pos, today }) {
  const tier = TIER_META[pos.risk_tier];
  const start = pos.purchase_date;
  const end = pos.maturity_date || pos.call_date || today;
  const todayPct = pct(start, end, today);
  const callPct = pos.call_date ? pct(start, end, pos.call_date) : null;

  return (
    <div className="px-5 py-4" data-testid={`timeline-row-${pos.ticker}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono tracking-widest ${tier.cls}`}>
            <tier.Icon className="w-3 h-3" /> {tier.label}
          </span>
          <div className="font-mono font-semibold text-paars-dark">{pos.ticker}</div>
          <div className="text-xs text-graphite/55 truncate max-w-[260px]">{pos.name || ""}</div>
        </div>
        <div className="flex items-center gap-6 text-xs">
          <div className="text-right">
            <div className="text-[10px] tracking-widest uppercase text-graphite/55">Price · Call</div>
            <div className="font-mono">{pos.current_price ? fmtMoney(pos.current_price) : "—"} · {fmtMoney(pos.call_price)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-widest uppercase text-graphite/55">YTC · YTM</div>
            <div className="font-mono"><span className={pos.ytc != null && pos.ytc < 0 ? "text-burgundy" : "text-gold-dark"}>{pos.ytc != null ? fmtPct(pos.ytc) : "—"}</span> · <span className="text-paars-dark">{pos.ytm != null ? fmtPct(pos.ytm) : "—"}</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-widest uppercase text-graphite/55">At risk</div>
            <div className={`font-mono ${pos.call_loss_total > 0 ? "text-burgundy" : "text-graphite/45"}`}>{fmtMoney(pos.call_loss_total)}</div>
          </div>
        </div>
      </div>

      {/* Bar */}
      <div className="relative h-6 bg-stone/60 border border-stone">
        {/* Background gradient — full bar */}
        <div className={`absolute inset-0 ${tier.bar} opacity-30`} />
        {/* "Today" marker */}
        {todayPct != null && (
          <div
            className="absolute top-0 bottom-0 w-px bg-graphite"
            style={{ left: `${todayPct}%` }}
            title={`Today · ${today}`}
          >
            <div className="absolute -top-4 -translate-x-1/2 text-[9px] font-mono tracking-widest text-graphite/70 uppercase whitespace-nowrap">TODAY</div>
          </div>
        )}
        {/* Call date marker */}
        {callPct != null && (
          <div
            className={`absolute top-0 bottom-0 w-px ${tier.bar}`}
            style={{ left: `${callPct}%` }}
            title={`Call date · ${pos.call_date}`}
          >
            <div className={`absolute -bottom-5 -translate-x-1/2 text-[9px] font-mono tracking-widest ${tier.text} uppercase whitespace-nowrap`}>CALL</div>
          </div>
        )}
        {/* Callable window shading (today → call_date if future) */}
        {todayPct != null && callPct != null && callPct > todayPct && (
          <div
            className={`absolute top-0 bottom-0 ${tier.bar} opacity-50`}
            style={{ left: `${todayPct}%`, width: `${callPct - todayPct}%` }}
          />
        )}
      </div>

      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-graphite/55">
          <CalendarIcon className="w-3 h-3" />
          {fmtDate(start)}
        </div>
        <div className="text-[10px] font-mono text-graphite/55">
          {callableCaption(pos)}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-graphite/55">
          {fmtDate(end)}
          <CalendarIcon className="w-3 h-3" />
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div data-testid="callrisk-empty" className="border border-stone bg-champagne p-10 text-center">
      <ShieldCheck className="w-10 h-10 text-groen-dark mx-auto mb-4" strokeWidth={1.5} />
      <h3 className="text-lg font-bold text-graphite mb-2">No callable positions</h3>
      <p className="text-sm text-graphite/65 max-w-md mx-auto mb-6">
        Call risk applies to preferred stocks and babybonds. Add a position and we&apos;ll surface
        any redemption risk here automatically.
      </p>
      <Link
        to="/holdings"
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-paars text-graphite text-xs font-semibold tracking-wider uppercase hover:bg-paars-light"
      >
        Go to Positions
      </Link>
    </div>
  );
}
